// University of South Florida: public college page (#2652), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - USF's Common Data Set 2025-2026 (usf-cds-2025-2026-final-v2.pdf, linked
//    from the Office of Decision Support's Common Data Set page; read from
//    the PDF and its rendered pages): C1 applicants, admits and enrollees for
//    Fall 2025 (university-wide); C8 test policy for Fall 2027; C21/C22
//    plans. Quirk: the C1 residency table reads 7,283 in-state applicants,
//    5,533 admitted and 5,484 enrolled (a 99% yield) against 63,880
//    out-of-state applicants; it does not hang together, so no residency rows
//    are shown. The G1/G5 costs in the file (posted October 2025) are not
//    used; the financial aid office's 2026-27 chart is.
//  - USF Admissions, Freshman pages: Dates and Deadlines (the Fall/Summer
//    2027 table), Academic Requirements (test rules, selective majors) and
//    How to Apply (the Honors College application). No change for fall 2028
//    entry was announced there.
//  - USF Office of Financial Aid, 2026-27 Cost of Attendance, students
//    living on campus (undergraduate, Florida resident and non-resident).
//  - College Scorecard, school id 137351 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirk: the CDS answers no to early action for the class it reports, but
// the fall 2027 dates page lists a nonbinding Early Action deadline. Both
// are stated as each source has them.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'USF Common Data Set 2025-2026',
        url: 'https://www.usf.edu/ods/data-tools/cds.aspx',
        read: '2026-10-01',
    },
    dates: {
        label: 'USF Admissions, Freshman Admission Dates and Deadlines',
        url: 'https://www.usf.edu/admissions/freshmen/admission-information/dates-deadlines.aspx',
        read: '2026-10-01',
    },
    requirements: {
        label: 'USF Admissions, Freshman Academic Requirements',
        url: 'https://www.usf.edu/admissions/freshmen/admission-information/academic-requirements.aspx',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'USF Admissions, How to Apply: Freshman Students',
        url: 'https://www.usf.edu/admissions/freshmen/admission-information/how-to-apply.aspx',
        read: '2026-10-01',
    },
    costs: {
        label: 'USF Office of Financial Aid, 2026-27 Cost of Attendance, Students Living on Campus',
        url: 'https://tup-ofa.forest.usf.edu/finaid/coa/2627_on_campus',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?137351-University-of-South-Florida',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-south-florida',
    kbId: 'university_of_south_florida',
    name: 'University of South Florida',
    shortName: 'USF',
    aliases: ['USF', 'South Florida'],
    location: 'Tampa, FL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (USF brand colors: USF Green, USF Gold);
    // decoration only.
    branding: {
        primary: '#006747',
        secondary: '#CFC493',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_south_florida_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 71722,
        admitted: 29196,
        enrolled: 6730,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'USF\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'For the class it reports, the Common Data Set answers no to both early decision and early action, so it gives no early counts or early acceptance rate. For fall 2027, USF\'s admissions office lists a nonbinding Early Action deadline (see the deadlines).',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to USF with a preferred campus and start term, which are not guaranteed. Some programs ask for more:',
        rows: [
            { school: 'Biomedical Engineering', requirement: 'A selective major with its own high school GPA and/or SAT, ACT or CLT requirements.', source: 'requirements' },
            { school: 'Nursing', requirement: 'A selective major with its own high school GPA and/or SAT, ACT or CLT requirements.', source: 'requirements' },
            { school: 'Judy Genshaft Honors College', requirement: 'Optional: once you apply to USF, you can submit a separate Honors College application.', source: 'howToApply' },
        ],
        source: 'dates',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'December 16' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2', decision: 'December 9', binding: false },
            { plan: 'Regular Decision', deadline: 'December 16', decision: 'February 10', binding: false },
            { plan: 'Rolling', deadline: 'March 1', decision: 'Rolling, starting in early March', binding: false },
        ],
        note: 'Materials (application fee or waiver, test scores and the STARS academic record) are due November 16 for Early Action, January 15 for Regular Decision and March 15 for Rolling. Applying by January 15 with all materials brings automatic merit scholarship review. Early Action is nonbinding and decides admit, deny or defer; Regular Decision decides admit, waitlist or deny. Applying Early Action gives priority for your preferred campus and term.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'USF requires official results from at least one entrance exam: the SAT, ACT or CLT. It superscores, using your highest section scores across all SAT, ACT and CLT test dates. You need minimum section scores of SAT 490 reading and writing and 480 math, ACT 19 reading, 17 English and 19 math, or CLT 38 verbal plus grammar and writing and 16 quantitative. The optional essay sections are not considered.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required for fall 2027 applicants, with scores due by July 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [5960, 18846] },
            { item: 'Housing (on campus)', amounts: [9638, 9638] },
            { item: 'Food (meal plan)', amounts: [5770, 5770] },
            { item: 'Books and supplies', amounts: [728, 728] },
            { item: 'Transportation', amounts: [2176, 2176] },
            { item: 'Personal expenses', amounts: [2976, 2976] },
        ],
        totals: [27248, 40134],
        note: 'For an undergraduate living on campus, fall and spring. Tuition and fees are the standard rates for 14 semester hours each term.',
        source: 'costs',
        netPrice: {
            average: 8648,
            byIncome: [
                { income: '$0 to $30,000', amount: 5565 },
                { income: '$30,001 to $48,000', amount: 3989 },
                { income: '$48,001 to $75,000', amount: 6040 },
                { income: '$75,001 to $110,000', amount: 11459 },
                { income: 'Over $110,000', amount: 17159 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from USF\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
